import { useMemo } from 'react';
import type { Asset } from '@/types/GraphqlSchema';
import { formatDateShort } from '@/utils/helpers';
import type { TimelineEvent } from '../types';

const getPeakProductionDate = (asset: Asset | null): string | null => {
  const hidePeakProductionDateCountries = ['AZE'];

  if (
    asset?.peakProductionDate &&
    asset?.country?.isoCode &&
    !hidePeakProductionDateCountries.includes(asset.country.isoCode)
  ) {
    return formatDateShort(asset.peakProductionDate);
  }

  return null;
};

const getExpectedProjectSanction = (asset: Asset | null): string | null => {
  const isPreSanction = asset?.developmentStatus?.name?.toLowerCase()?.includes('pre-sanction');
  if (!isPreSanction) {
    return null;
  }

  const currentYear = new Date().getFullYear();
  const sanctionYear = asset?.expectedProjectSanction;

  if (!sanctionYear || sanctionYear < currentYear) {
    return null;
  }

  return String(sanctionYear);
};

export const useTimelineEvents = (asset: Asset | null): TimelineEvent[] => {
  return useMemo(() => {
    if (!asset) {
      return [];
    }

    return [
      {
        label: 'Discovery Date',
        date: formatDateShort(asset.discoveryDate),
      },
      {
        label: 'Expected Project Sanction',
        date: getExpectedProjectSanction(asset),
      },
      {
        label: 'First Production',
        date: formatDateShort(asset.firstProductionDate),
      },
      {
        label: 'Peak Production',
        date: getPeakProductionDate(asset),
      },
      {
        label: 'License Expiration',
        date: formatDateShort(asset.licenseExpirationDate),
      },
    ];
  }, [asset]);
};
